<template>
  <div class="page-container">
    <PageHeader title="用户管理">
      <template #actions>
        <el-button type="primary" size="small" icon="el-icon-plus" @click="handleAdd">新增用户</el-button>
      </template>
    </PageHeader>

    <SearchPanel>
      <el-input v-model.trim="query.auName" placeholder="用户名称" size="small" clearable></el-input>
      <el-input v-model.trim="query.auAccount" placeholder="账号" size="small" clearable></el-input>
      <el-select v-model="query.auPost" clearable placeholder="岗位" size="small" @change="handleSearch">
        <el-option v-for="item in postOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
      </el-select>
      <template #actions>
        <el-button size="small" @click="resetQuery">重置</el-button>
        <el-button type="primary" size="small" @click="handleSearch">查询</el-button>
      </template>
    </SearchPanel>

    <TablePanel>
      <aj-table
        v-loading="loading"
        :data="list"
        :columns="columns"
        :current-page.sync="query.currentPage"
        :page-size.sync="query.pageSize"
        :page-sizes="[10, 20, 30, 50]"
        :total="total"
        row-id-key="auId"
        stripe
        use-internal-pagination
        @page-change="handlePageChange"
        @operate-btn-click="handleOperateBtnClick"
      ></aj-table>
      <EmptyState v-if="!loading && list.length === 0" title="暂无用户" icon="el-icon-user"></EmptyState>
    </TablePanel>

    <aj-form-add
      :dialog-visible.sync="addDialogVisible"
      :form-config="userAddFormConfig"
      :form-data="addFormData"
      dialog-title="新增用户"
      dialog-width="45%"
      :confirm-loading="submitLoading"
      @confirm="handleAddSubmit"
    />

    <aj-form-edit
      :dialog-visible.sync="editDialogVisible"
      :form-config="userEditFormConfig"
      :form-data="editFormData"
      dialog-title="编辑用户"
      dialog-width="45%"
      :confirm-loading="submitLoading"
      @confirm="handleEditSubmit"
    />

    <aj-form-edit
      :dialog-visible.sync="pwdDialogVisible"
      :form-config="pwdFormConfig"
      :form-data="pwdFormData"
      dialog-title="重置密码"
      dialog-width="45%"
      confirm-text="确定"
      :confirm-loading="pwdSubmitLoading"
      @confirm="handlePwdSubmit"
    />
  </div>
</template>

<script>
import { getRtUserList, addRtUser, updateRtUser, deleteRtUser, updateRtUserPwAdmin, getRtRoleList, getDepartmentList } from '@/api/system'
import AjFormAdd from '@/components/ajForm/ajFormAdd.vue'
import AjFormEdit from '@/components/ajForm/ajFormEdit.vue'
import AjTable from '@/components/ajTable/ajTable.vue'
import EmptyState from '@/components/EmptyState.vue'
import PageHeader from '@/components/PageHeader.vue'
import SearchPanel from '@/components/SearchPanel.vue'
import TablePanel from '@/components/TablePanel.vue'

const POST_OPTIONS = [
  { label: '系统管理员', value: 1 },
  { label: '用户', value: 2 },
]

// 部门级联选择器配置
const DEPT_CASCADER_PROPS = {
  checkStrictly: true,
  value: 'adId',
  label: 'adName',
  children: 'children',
  emitPath: false
}

const BASE_COLUMNS = [
  { type: 'index', label: '序号', width: 60 },
  { prop: 'auName', label: '姓名', minWidth: 100 },
  { prop: 'auAccount', label: '账号', minWidth: 120 },
  { prop: 'auPostName', label: '岗位', minWidth: 100 },
  { prop: 'auRoleNames', label: '角色', minWidth: 140 },
  { prop: 'auEmail', label: '邮箱', minWidth: 160 },
  { prop: 'createtime', label: '创建时间', width: 160 }
]

function createEmptyUserFormData () {
  return { auId: null, auName: '', auAccount: '', auPost: 2, auEmail: '', auPw: '', selectedRoleIds: [], adId: null }
}

function createEmptyPwdFormData () {
  return { auId: null, auPw: '' }
}

export default {
  name: 'SystemUserPage',
  components: { AjFormAdd, AjFormEdit, AjTable, EmptyState, PageHeader, SearchPanel, TablePanel },
  data () {
    return {
      loading: false,
      submitLoading: false,
      pwdSubmitLoading: false,
      list: [],
      total: 0,
      postOptions: POST_OPTIONS,
      roleOptions: [],
      deptTreeOptions: [],
      query: { auName: '', auAccount: '', auPost: '', currentPage: 1, pageSize: 10 },
      addDialogVisible: false,
      editDialogVisible: false,
      pwdDialogVisible: false,
      addFormData: createEmptyUserFormData(),
      editFormData: createEmptyUserFormData(),
      pwdFormData: createEmptyPwdFormData()
    }
  },
  computed: {
    columns () {
      return [
        ...BASE_COLUMNS,
        {
          type: 'operation',
          prop: 'operation',
          label: '操作',
          width: 220,
          fixed: 'right',
          buttons: [
            { key: 'edit', text: '编辑', type: 'text' },
            { key: 'resetPwd', text: '重置密码', type: 'text' },
            { key: 'delete', text: '删除', type: 'text', class: 'text-danger' }
          ]
        }
      ]
    },
    roleSelectOptions () {
      return this.roleOptions.map(item => ({ label: item.roleName, value: item.roleId }))
    },
    userAddFormConfig () {
      return [
        [
          { label: '姓名', prop: 'auName', type: 'input', span: 24, required: true },
          { label: '账号', prop: 'auAccount', type: 'input', span: 24, required: true }
        ],
        [
          { label: '部门', prop: 'adId', type: 'cascader', span: 24, options: this.deptTreeOptions, props: DEPT_CASCADER_PROPS, clearable: true, placeholder: '请选择部门' },
          { label: '岗位', prop: 'auPost', type: 'select', span: 24, options: this.postOptions, required: true }
        ],
        [
          { label: '邮箱', prop: 'auEmail', type: 'input', span: 24 },
          { label: '角色', prop: 'selectedRoleIds', type: 'select', span: 24, multiple: true, options: this.roleSelectOptions }
        ],
        [
          { label: '密码', prop: 'auPw', type: 'input', inputType: 'password', showPassword: true, span: 24, placeholder: '请设置密码' }
        ]
      ]
    },
    userEditFormConfig () {
      return [
        [
          { label: '姓名', prop: 'auName', type: 'input', span: 24, required: true },
          { label: '账号', prop: 'auAccount', type: 'input', span: 24, required: true, disabled: true }
        ],
        [
          { label: '部门', prop: 'adId', type: 'cascader', span: 24, options: this.deptTreeOptions, props: DEPT_CASCADER_PROPS, clearable: true, placeholder: '请选择部门' },
          { label: '岗位', prop: 'auPost', type: 'select', span: 24, options: this.postOptions, required: true }
        ],
        [
          { label: '邮箱', prop: 'auEmail', type: 'input', span: 24 },
          { label: '角色', prop: 'selectedRoleIds', type: 'select', span: 24, multiple: true, options: this.roleSelectOptions }
        ]
      ]
    },
    pwdFormConfig () {
      return [
        [
          { label: '新密码', prop: 'auPw', type: 'input', inputType: 'password', showPassword: true, span: 24, required: true }
        ]
      ]
    }
  },
  created () {
    this.fetchList()
    this.fetchRoleOptions()
    this.fetchDeptOptions()
  },
  methods: {
    async fetchList () {
      this.loading = true
      try {
        const res = await getRtUserList(this.query)
        this.list = Array.isArray(res.data) ? res.data : []
        this.total = Number(res.total || 0)
      } catch (error) {
        this.list = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    async fetchRoleOptions () {
      try {
        const res = await getRtRoleList({ currentPage: 1, pageSize: 500 })
        this.roleOptions = Array.isArray(res.data) ? res.data : []
      } catch (error) {
        this.roleOptions = []
      }
    },
    async fetchDeptOptions () {
      try {
        const res = await getDepartmentList()
        const list = Array.isArray(res.data) ? res.data : []
        this.deptTreeOptions = this.buildDeptTreeOptions(list)
      } catch (error) {
        this.deptTreeOptions = []
      }
    },
    buildDeptTreeOptions (list) {
      return list.map(item => ({
        adId: item.adId,
        adName: item.adName,
        children: item.children && item.children.length > 0 ? this.buildDeptTreeOptions(item.children) : []
      }))
    },
    handleSearch () {
      this.query.currentPage = 1
      this.fetchList()
    },
    resetQuery () {
      this.query = { auName: '', auAccount: '', auPost: '', currentPage: 1, pageSize: 10 }
      this.fetchList()
    },
    handlePageChange ({ currentPage, pageSize }) {
      this.query.currentPage = currentPage
      this.query.pageSize = pageSize
      this.fetchList()
    },
    handleOperateBtnClick ({ btn, row }) {
      switch (btn.key) {
        case 'edit': this.handleEdit(row); break
        case 'resetPwd': this.handleResetPwd(row); break
        case 'delete': this.handleDelete(row); break
      }
    },
    getPostTagType (post) {
      const map = { 1: 'danger', 2: '', 3: 'warning', 4: 'success' }
      return map[post] || ''
    },
    handleAdd () {
      this.addFormData = createEmptyUserFormData()
      this.addDialogVisible = true
    },
    handleEdit (row) {
      let roleIds = []
      if (row.auRoleIds) {
        try {
          roleIds = JSON.parse(row.auRoleIds)
        } catch (e) {
          roleIds = []
        }
      }
      this.editFormData = {
        auId: row.auId,
        auName: row.auName,
        auAccount: row.auAccount,
        auPost: row.auPost || 2,
        auEmail: row.auEmail || '',
        auPw: '',
        selectedRoleIds: roleIds,
        adId: row.adId || null
      }
      this.editDialogVisible = true
    },
    buildUserPayload (formData) {
      return {
        auId: formData.auId,
        auName: formData.auName,
        auAccount: formData.auAccount,
        auPost: formData.auPost,
        auEmail: formData.auEmail,
        auRoleIds: JSON.stringify(formData.selectedRoleIds || []),
        adId: formData.adId || null
      }
    },
    async handleAddSubmit ({ data }) {
      this.submitLoading = true
      try {
        const payload = this.buildUserPayload(data)
        if (data.auPw) payload.auPw = data.auPw
        await addRtUser(payload)
        this.$message.success('用户创建成功')
        this.addDialogVisible = false
        this.fetchList()
      } finally {
        this.submitLoading = false
      }
    },
    async handleEditSubmit ({ data }) {
      this.submitLoading = true
      try {
        await updateRtUser(this.buildUserPayload(data))
        this.$message.success('用户更新成功')
        this.editDialogVisible = false
        this.fetchList()
      } finally {
        this.submitLoading = false
      }
    },
    async handleDelete (row) {
      try {
        await this.$confirm('确认删除该用户吗？', '提示', { type: 'warning' })
        await deleteRtUser({ auId: row.auId })
        this.$message.success('用户已删除')
        this.fetchList()
      } catch (error) {}
    },
    handleResetPwd (row) {
      this.pwdFormData = { auId: row.auId, auPw: '' }
      this.pwdDialogVisible = true
    },
    async handlePwdSubmit ({ data }) {
      this.pwdSubmitLoading = true
      try {
        await updateRtUserPwAdmin(data)
        this.$message.success('密码重置成功')
        this.pwdDialogVisible = false
      } finally {
        this.pwdSubmitLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.page-container {
  background: #fff;
  border-radius: 10px;
  padding: 20px;
  border: 1px solid #d1d5db;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.user-actions {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.text-danger { color: #ef4444 !important; }

::v-deep .aj-table-operation-link {
  margin: 0 4px;
  font-size: 12px;
  font-weight: 500;

  &.text-danger {
    color: #ef4444 !important;
    &:hover { color: #dc2626 !important; }
  }

  &:not(.text-danger):hover { color: #4f7cff !important; }
}
</style>
